import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Search, Shield, Store } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { api } from "@/lib/api";
import { classifyErr } from "@/lib/admin";
import { useAuth } from "@/lib/auth";

export default function UserForm({ open, onOpenChange, editing, roles, zones, onCreated, onUpdated }) {
  const { user: actingUser } = useAuth();
  const isSuperAdminActing = actingUser?.role_code === "SUPER_ADMIN";
  const isEdit = !!editing;
  const [form, setForm] = useState(defaultState());
  const [managers, setManagers] = useState([]);
  const [zoneAreas, setZoneAreas] = useState([]);      // areas in the currently-selected zones
  const [mappedDists, setMappedDists] = useState(null);   // distributors mapped to this user
  const [knownAreas, setKnownAreas] = useState({});    // id → {name,zone_id} lookup for chip labels
  useEffect(() => {
    if (!editing?.id) { setMappedDists(null); return undefined; }
    let live = true;
    api.get(`/user-distributor-map/users/${editing.id}/distributors`)
      .then((r) => { if (live) setMappedDists((r.data.items || []).filter((d) => d.distributor_is_active !== false)); })
      .catch(() => { if (live) setMappedDists([]); });
    return () => { live = false; };
  }, [editing?.id]);
  const [zoneSubZones, setZoneSubZones] = useState([]);  // sub-zones in the currently-selected zones
  const [knownSubZones, setKnownSubZones] = useState({}); // id → {name} lookup for chip labels
  const [policyGroups, setPolicyGroups] = useState([]);

  // Phase 26F.2 — new inline panels
  const [permCatalog, setPermCatalog] = useState({ items: [], groups: null });
  const [permsGranted, setPermsGranted] = useState([]);            // current user's set
  const [permsBaseline, setPermsBaseline] = useState([]);          // for dirty-check
  const [permsSavePending, setPermsSavePending] = useState(false);
  const [distributors, setDistributors] = useState([]);
  // id -> {code,name} for EVERY distributor ever seen this session — never
  // shrinks, unlike `distributors` (which narrows to the current Zone/
  // Sub-Zone scope for the picker list). Chip labels for already-mapped
  // distributors outside the current scope resolve from this instead of
  // falling back to a raw id, same idea as knownAreas/knownSubZones above.
  const [knownDistributors, setKnownDistributors] = useState({});
  const [distSearch, setDistSearch] = useState("");
  const [distMapped, setDistMapped] = useState([]);                // current set
  const [distBaseline, setDistBaseline] = useState([]);            // for dirty-check
  const [distSavePending, setDistSavePending] = useState(false);

  // Confirm dialog for area removal on users with recent activity
  const [confirmArea, setConfirmArea] = useState(null);            // {id,name,recentCount}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function defaultState(u) {
    return {
      name: u?.name || "",
      email: u?.email || "",
      phone: u?.phone || "",
      role_code: u?.role_code || "",
      reporting_manager_id: u?.reporting_manager_id || "",
      assigned_zone_ids: u?.assigned_zone_ids || [],
      assigned_area_ids: u?.assigned_area_ids || [],
      assigned_sub_zone_ids: u?.assigned_sub_zone_ids || [],
      policy_group_id: u?.policy_group_id || "",
      tada_max_backdate_days: u?.tada_max_backdate_days ?? "",
      initial_password: "",
    };
  }

  useEffect(() => {
    if (!open) return;
    setForm(defaultState(editing));
    setError("");
    setDistSearch("");
    setConfirmArea(null);
    api.get("/travel-policy-groups", { params: { is_active: true, page_size: 200 } })
      .then((r) => setPolicyGroups(r.data.items || [])).catch(() => setPolicyGroups([]));
    // Phase 26F.2 — workflow permissions catalog
    api.get("/workflow-permissions").then((r) => setPermCatalog({
      items: r.data.items || [],
      groups: r.data.groups || null,
    })).catch(() => setPermCatalog({ items: [], groups: null }));

    if (isEdit && editing?.id) {
      api.get(`/users/${editing.id}/workflow-permissions`).then((r) => {
        const codes = r.data.permission_codes || [];
        setPermsGranted(codes); setPermsBaseline(codes);
      }).catch(() => { setPermsGranted([]); setPermsBaseline([]); });
      api.get(`/user-distributor-map/users/${editing.id}/distributors`, { params: { page_size: 200 } })
        .then((r) => {
          const ids = (r.data.items || []).map((m) => m.distributor_id);
          setDistMapped(ids); setDistBaseline(ids);
        }).catch(() => { setDistMapped([]); setDistBaseline([]); });
    } else {
      setPermsGranted([]); setPermsBaseline([]);
      setDistMapped([]); setDistBaseline([]);
    }
  }, [open, editing, isEdit]);

  const selectedRole = useMemo(
    () => roles.find((r) => r.code === form.role_code) || null,
    [roles, form.role_code],
  );

  useEffect(() => {
    if (!selectedRole) { setManagers([]); return; }
    (async () => {
      try {
        // page_size 500 (the endpoint's max) — with 267+ distributor logins
        // now in the system, a page_size of 100 returned distributors only
        // and SE/ASM/ZSM never showed up as manager candidates at all.
        const { data } = await api.get("/users", { params: { page: 1, page_size: 500 } });
        const roleByCode = Object.fromEntries(roles.map((r) => [r.code, r]));
        // A Super Admin can override the org chart for any user — so when
        // they're the one editing, offer every other active user as a
        // candidate manager instead of the normal "strictly higher in the
        // hierarchy" rule, which leaves some roles (e.g. Distributor, or
        // Admin itself) with no candidates and the field permanently
        // disabled.
        if (isSuperAdminActing) {
          setManagers(data.items.filter(
            (u) => u.status === "active" && u.id !== editing?.id,
          ));
          return;
        }
        setManagers(data.items.filter(
          (u) => u.status === "active" &&
                 (roleByCode[u.role_code]?.hierarchy_level ?? 99) < selectedRole.hierarchy_level,
        ));
      } catch (_e) { setManagers([]); }
    })();
  }, [selectedRole, roles, editing, isSuperAdminActing]);

  // Load areas for currently-selected zones AND for any areas the user is
  // already assigned to but whose zone is no longer selected (so chip labels
  // still resolve). Phase 26F.2.
  //
  // Once one or more Sub-Zones are picked, narrow to just those sub-zones
  // instead of the whole zone — a zone can span far more areas than the
  // rep actually covers, and picking a sub-zone was previously a no-op for
  // this list (it only ever narrowed by zone_id).
  useEffect(() => {
    (async () => {
      try {
        const all = [];
        if (form.assigned_sub_zone_ids?.length) {
          for (const szid of form.assigned_sub_zone_ids) {
            const { data } = await api.get("/geography/areas", { params: { sub_zone_id: szid } });
            all.push(...(data.items || []));
          }
        } else {
          for (const zid of form.assigned_zone_ids || []) {
            const { data } = await api.get("/geography/areas", { params: { zone_id: zid } });
            all.push(...(data.items || []));
          }
        }
        setZoneAreas(all);
        setKnownAreas((prev) => {
          const merged = { ...prev };
          for (const a of all) merged[a.id] = { name: a.name, zone_id: a.zone_id };
          return merged;
        });
      } catch (_e) { setZoneAreas([]); }
    })();
  }, [form.assigned_zone_ids, form.assigned_sub_zone_ids]);

  // On edit-open, resolve names for pre-assigned areas that may be outside
  // the current zone list (rare but possible after a zone was unassigned).
  useEffect(() => {
    if (!isEdit || !form.assigned_area_ids?.length) return;
    const missing = form.assigned_area_ids.filter((id) => !knownAreas[id]);
    if (!missing.length) return;
    // /geography/areas doesn't accept id[] — read each of the user's zones INCLUDING inactive
    // areas, so a saved area that is no longer active still shows its name, not a raw id.
    (async () => {
      try {
        const found = {};
        for (const zid of form.assigned_zone_ids || []) {
          const { data } = await api.get("/geography/areas", { params: { zone_id: zid, include_inactive: true } });
          for (const a of data.items || []) {
            if (missing.includes(a.id)) found[a.id] = { name: a.is_active === false ? `${a.name} (inactive)` : a.name, zone_id: a.zone_id };
          }
        }
        if (Object.keys(found).length) setKnownAreas((prev) => ({ ...prev, ...found }));
      } catch (_e) { /* keep the id fallback */ }
    })();
  }, [isEdit, form.assigned_area_ids, form.assigned_zone_ids, knownAreas]);

  // Load sub-zones for the currently-selected zones (Zone -> State -> Sub
  // Zone; the endpoint resolves zone_id via states internally). Mirrors the
  // Areas cascade above.
  useEffect(() => {
    (async () => {
      try {
        const all = [];
        for (const zid of form.assigned_zone_ids || []) {
          const { data } = await api.get("/geography/sub-zones", { params: { zone_id: zid } });
          all.push(...(data.items || []));
        }
        setZoneSubZones(all);
        setKnownSubZones((prev) => {
          const merged = { ...prev };
          for (const s of all) merged[s.id] = { name: s.name };
          return merged;
        });
      } catch (_e) { setZoneSubZones([]); }
    })();
  }, [form.assigned_zone_ids]);

  // Distributor Mappings picker — same narrowing idea as Areas above: once
  // a Sub-Zone is picked, only its distributors show (a zone can span far
  // more distributors than any one rep should be mapped against); with a
  // Zone but no Sub-Zone yet, narrow to that zone; with neither picked yet
  // (e.g. a brand-new user form), fall back to every distributor so the
  // picker isn't empty before any scope is chosen.
  useEffect(() => {
    (async () => {
      try {
        let all = [];
        if (form.assigned_sub_zone_ids?.length) {
          for (const szid of form.assigned_sub_zone_ids) {
            const { data } = await api.get("/distributors", { params: { sub_zone_id: szid, page_size: 2000 } });
            all.push(...(data.items || []));
          }
        } else if (form.assigned_zone_ids?.length) {
          for (const zid of form.assigned_zone_ids) {
            const { data } = await api.get("/distributors", { params: { zone_id: zid, page_size: 2000 } });
            all.push(...(data.items || []));
          }
        } else {
          const { data } = await api.get("/distributors", { params: { page_size: 2000 } });
          all = data.items || [];
        }
        // De-dupe — a distributor mapped to a multi-select of overlapping
        // scopes (or already-mapped ones outside current scope) shouldn't
        // appear twice.
        const byId = new Map(all.map((d) => [d.id, d]));
        setDistributors([...byId.values()]);
        setKnownDistributors((prev) => {
          const merged = { ...prev };
          for (const d of all) merged[d.id] = { code: d.code, name: d.name };
          return merged;
        });
      } catch (_e) { setDistributors([]); }
    })();
  }, [form.assigned_zone_ids, form.assigned_sub_zone_ids]);

  // On edit-open, resolve names for already-mapped distributors that fall
  // outside whatever Zone/Sub-Zone scope the picker above ends up narrowed
  // to — otherwise their chips silently show a raw id instead of a name
  // once the picker list itself no longer happens to include them.
  useEffect(() => {
    if (!distMapped.length) return;
    const missing = distMapped.filter((id) => !knownDistributors[id]);
    if (!missing.length) return;
    api.get("/distributors", { params: { ids: missing.join(","), page_size: missing.length } })
      .then((r) => {
        const items = r.data.items || [];
        if (!items.length) return;
        setKnownDistributors((prev) => {
          const merged = { ...prev };
          for (const d of items) merged[d.id] = { code: d.code, name: d.name };
          return merged;
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distMapped]);

  const toggleZone = (id) => {
    setForm((f) => {
      const has = f.assigned_zone_ids.includes(id);
      const next = has ? f.assigned_zone_ids.filter((x) => x !== id) : [...f.assigned_zone_ids, id];
      // Do NOT wipe assigned_area_ids/assigned_sub_zone_ids on zone change —
      // user can review/remove chips.
      return { ...f, assigned_zone_ids: next };
    });
  };
  const toggleArea = (id) => {
    setForm((f) => ({
      ...f,
      assigned_area_ids: f.assigned_area_ids.includes(id)
        ? f.assigned_area_ids.filter((x) => x !== id)
        : [...f.assigned_area_ids, id],
    }));
  };
  const toggleSubZone = (id) => {
    setForm((f) => ({
      ...f,
      assigned_sub_zone_ids: f.assigned_sub_zone_ids.includes(id)
        ? f.assigned_sub_zone_ids.filter((x) => x !== id)
        : [...f.assigned_sub_zone_ids, id],
    }));
  };

  // Phase 26F.2 — area removal with (non-blocking) confirm on active data.
  const startRemoveArea = async (id) => {
    if (!isEdit) { toggleArea(id); return; }
    try {
      // Non-blocking pre-check: count recent visits + attendance for this user × area.
      const [v, a] = await Promise.all([
        api.get("/visits", { params: { user_id: editing.id, area_id: id, page_size: 1 } }).catch(() => ({ data: { total: 0 } })),
        api.get("/attendance", { params: { user_id: editing.id, area_id: id, page_size: 1 } }).catch(() => ({ data: { total: 0 } })),
      ]);
      const recent = (v.data?.total || 0) + (a.data?.total || 0);
      if (recent > 0) {
        setConfirmArea({ id, name: knownAreas[id]?.name || id, recentCount: recent });
        return;
      }
    } catch { /* fall through — remove without confirmation */ }
    toggleArea(id);
  };

  const genPassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let s = "";
    for (let i = 0; i < 12; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setForm((f) => ({ ...f, initial_password: s }));
  };

  // --- Workflow permissions grouping ---
  // The `/api/workflow-permissions` payload is:
  //   { items: [{code, name, group_key, order_index, …}],
  //     groups: { group_key: display_label, … } }
  // We group items by `group_key` and title each group by its display label.
  const grouped = useMemo(() => {
    const items = permCatalog.items || [];
    const groups = permCatalog.groups;
    if (groups && typeof groups === "object" && !Array.isArray(groups)) {
      const byKey = {};
      for (const it of items) {
        const k = it.group_key || "_other";
        if (!byKey[k]) byKey[k] = [];
        byKey[k].push(it);
      }
      const order = Object.keys(groups);
      const other = Object.keys(byKey).filter((k) => !order.includes(k));
      return [...order, ...other]
        .filter((k) => byKey[k] && byKey[k].length > 0)
        .map((k) => ({
          label: groups[k] || k,
          items: byKey[k].sort((a, b) => (a.order_index || 0) - (b.order_index || 0)),
        }));
    }
    return [{ label: "Workflow permissions", items }];
  }, [permCatalog]);

  const togglePerm = (code) => {
    setPermsGranted((p) => (p.includes(code) ? p.filter((x) => x !== code) : [...p, code]));
  };

  const permsDirty = useMemo(() => {
    const a = new Set(permsGranted); const b = new Set(permsBaseline);
    if (a.size !== b.size) return true;
    for (const x of a) if (!b.has(x)) return true;
    return false;
  }, [permsGranted, permsBaseline]);

  const distDirty = useMemo(() => {
    const a = new Set(distMapped); const b = new Set(distBaseline);
    if (a.size !== b.size) return true;
    for (const x of a) if (!b.has(x)) return true;
    return false;
  }, [distMapped, distBaseline]);

  // --- Distributor mapping helpers ---
  const filteredDistributors = useMemo(() => {
    const q = distSearch.trim().toLowerCase();
    if (!q) return distributors;
    return distributors.filter((d) =>
      (d.name || "").toLowerCase().includes(q) || (d.code || "").toLowerCase().includes(q),
    );
  }, [distributors, distSearch]);

  const toggleDist = (id) => {
    setDistMapped((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  };

  const distName = (id) => {
    const known = knownDistributors[id];
    if (known) return `${known.code || ""} · ${known.name}`;
    const d = distributors.find((x) => x.id === id);
    return d ? `${d.code || ""} · ${d.name}` : id;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      let saved;
      if (isEdit) {
        const payload = {
          name: form.name,
          email: form.email || null,
          phone: form.phone || null,
          role_code: form.role_code,
          reporting_manager_id: form.reporting_manager_id || null,
          assigned_zone_ids: form.assigned_zone_ids,
          assigned_area_ids: form.assigned_area_ids,   // may be []
          assigned_sub_zone_ids: form.assigned_sub_zone_ids,   // may be []
          policy_group_id: form.policy_group_id || null,
          tada_max_backdate_days: form.tada_max_backdate_days === "" ? null : Number(form.tada_max_backdate_days),
        };
        const { data } = await api.patch(`/users/${editing.id}`, payload);
        saved = data;
        // Persist perms + distributor mappings ONLY when dirty (idempotency).
        if (permsDirty) {
          setPermsSavePending(true);
          await api.put(`/users/${editing.id}/workflow-permissions`,
            { permission_codes: permsGranted });
          setPermsBaseline(permsGranted);
          setPermsSavePending(false);
        }
        if (distDirty) {
          setDistSavePending(true);
          await api.post("/user-distributor-map/replace",
            { user_id: editing.id, distributor_ids: distMapped });
          setDistBaseline(distMapped);
          setDistSavePending(false);
        }
        toast.success("User updated");
        onUpdated?.(saved);
      } else {
        const payload = {
          name: form.name,
          email: form.email || null,
          phone: form.phone || null,
          initial_password: form.initial_password || null,
          role_code: form.role_code,
          reporting_manager_id: form.reporting_manager_id || null,
          assigned_zone_ids: form.assigned_zone_ids,
          assigned_area_ids: form.assigned_area_ids,   // may be []
          assigned_sub_zone_ids: form.assigned_sub_zone_ids,   // may be []
          policy_group_id: form.policy_group_id || null,
        };
        const { data } = await api.post("/users", payload);
        // If the admin pre-set perms or distributors on create, apply them right after.
        if (permsGranted.length > 0) {
          await api.put(`/users/${data.id}/workflow-permissions`,
            { permission_codes: permsGranted }).catch(() => {});
        }
        if (distMapped.length > 0) {
          await api.post("/user-distributor-map/replace",
            { user_id: data.id, distributor_ids: distMapped }).catch(() => {});
        }
        toast.success("User created");
        onCreated?.({ user: data, initial_password: data.initial_password });
      }
    } catch (err) {
      setError(classifyErr(err));
      setPermsSavePending(false);
      setDistSavePending(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-user-form" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit user" : "New user"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update user details, role, mappings, and workflow permissions." : "Create a new office or field user. They will be forced to change password on first login."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5" noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Full name</Label>
              <Input data-testid="uf-name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     required className="h-10 mt-1" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="uf-email" type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="h-10 mt-1" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="uf-phone" value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     placeholder="+91…" className="h-10 mt-1" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role_code}
                      onValueChange={(v) => setForm({ ...form, role_code: v, reporting_manager_id: "" })}>
                <SelectTrigger data-testid="uf-role" className="h-10 mt-1">
                  <SelectValue placeholder="Select a role…" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.code} value={r.code}>
                      {r.name} <span className="text-neutral-400 ml-1">({r.login_type})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedRole?.login_type === "email" && !form.email && (
                <p className="text-[11px] text-amber-600 mt-1">This role requires an email.</p>
              )}
              {selectedRole?.login_type === "phone" && !form.phone && (
                <p className="text-[11px] text-amber-600 mt-1">This role requires a phone number.</p>
              )}
            </div>
            <div>
              <Label>Reporting manager</Label>
              <SearchableSelect
                value={form.reporting_manager_id || ""}
                onChange={(v) => setForm({ ...form, reporting_manager_id: v })}
                disabled={!selectedRole || managers.length === 0}
                placeholder="—"
                emptyMessage="No match."
                testid="uf-manager"
                className="h-10 mt-1"
                options={managers.map((m) => ({ id: m.id, label: `${m.name} (${m.role_code})` }))}
              />
            </div>
            <div>
              <Label>Travel policy group</Label>
              <Select value={form.policy_group_id || "none"}
                      onValueChange={(v) => setForm({ ...form, policy_group_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="uf-policy-group" className="h-10 mt-1">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none (legacy rates) —</SelectItem>
                  {policyGroups.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`uf-policy-option-${p.code}`}>
                      <span className="font-mono text-xs mr-2">{p.code}</span>{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-neutral-500 mt-1">Overrides legacy DA/travel rates for TA/DA claims.</p>
            </div>
            <div>
              <Label>TA/DA max backdate (days)</Label>
              <Input type="number" min={0} max={90}
                     placeholder="Org default"
                     value={form.tada_max_backdate_days}
                     onChange={(e) => setForm({ ...form, tada_max_backdate_days: e.target.value })}
                     data-testid="uf-tada-max-backdate-days"
                     className="h-10 mt-1" />
              <p className="text-[11px] text-neutral-500 mt-1">
                Overrides the org-wide TA/DA setting for this user only. Leave blank to use
                the org default (Admin → Masters → TA/DA Settings). 0 = same-day only for this user.
              </p>
            </div>
          </div>

          <div>
            <Label>Zones</Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {zones.map((z) => (
                <label key={z.id} data-testid={`uf-zone-${z.code}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                          form.assigned_zone_ids.includes(z.id)
                            ? "border-[#E5197F] bg-[#E5197F]/5"
                            : "border-neutral-200 hover:bg-neutral-50"
                        }`}>
                  <Checkbox checked={form.assigned_zone_ids.includes(z.id)}
                             onCheckedChange={() => toggleZone(z.id)} />
                  <span>{z.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* A distributor is its own area, so users are scoped by their distributor mapping:
              every beat and outlet of a mapped distributor belongs to the user automatically. */}
          <div data-testid="uf-distributors-section">
            <Label>Distributors <span className="text-[11px] font-normal text-neutral-500">(from the distributor mapping)</span></Label>
            {!isEdit ? (
              <p className="mt-1 text-[11px] text-neutral-500">Save the user first, then map distributors from Users → Distributor mapping. All beats of a mapped distributor become the user's beats automatically.</p>
            ) : mappedDists === null ? (
              <p className="mt-1 text-[11px] text-neutral-400">Loading…</p>
            ) : mappedDists.length === 0 ? (
              <p className="mt-1 text-[11px] text-neutral-500">No distributor mapped yet — map from Users → Distributor mapping.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="uf-mapped-distributors">
                {mappedDists.map((d) => (
                  <span key={d.distributor_id || d.map_id}
                        className="inline-flex items-center rounded-md border border-[#6D2C91]/40 bg-[#6D2C91]/5 px-2 py-0.5 text-xs text-[#6D2C91]">
                    {d.distributor_name || d.distributor_code || "—"}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Sub Zones: assigning any restricts this user's data visibility
              to only those sub-zones (within their assigned zone(s)) —
              records outside them stay hidden even from managers who'd
              otherwise see their whole zone. */}
          <div data-testid="uf-sub-zones-section">
            <div className="flex items-baseline gap-2">
              <Label>Sub Zones <span className="text-[11px] font-normal text-neutral-500">(optional)</span></Label>
              {form.assigned_sub_zone_ids.length === 0 && (
                <span className="text-[11px] text-neutral-500">— no sub-zones assigned; user is zone-scoped only.</span>
              )}
            </div>

            {form.assigned_sub_zone_ids.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="uf-current-sub-zones">
                {form.assigned_sub_zone_ids.map((id) => (
                  <span key={id}
                         className="inline-flex items-center gap-1 rounded-md border border-[#6D2C91]/40 bg-[#6D2C91]/5 px-2 py-0.5 text-xs text-[#6D2C91]"
                         data-testid={`uf-sub-zone-chip-${id}`}>
                    {knownSubZones[id]?.name || id}
                    <button type="button" onClick={() => toggleSubZone(id)}
                             className="rounded p-0.5 hover:bg-[#6D2C91]/15"
                             aria-label="Remove sub-zone mapping"
                             data-testid={`uf-sub-zone-remove-${id}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {zoneSubZones.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto"
                    data-testid="uf-available-sub-zones">
                {zoneSubZones.filter((s) => !form.assigned_sub_zone_ids.includes(s.id)).map((s) => (
                  <label key={s.id}
                          className="flex items-center gap-2 px-2.5 py-1 rounded-md border border-dashed border-neutral-300 cursor-pointer text-xs text-neutral-600 hover:bg-neutral-50"
                          data-testid={`uf-sub-zone-add-${s.id}`}>
                    <Checkbox checked={false} onCheckedChange={() => toggleSubZone(s.id)} />
                    <span>+ {s.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Phase 26F.2 — inline Workflow Permissions & Distributor Mappings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-neutral-200">
            <div data-testid="uf-perms-panel">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-[#6D2C91]" />
                <Label className="text-sm">Workflow Permissions</Label>
                {permsDirty && (
                  <span className="text-[10px] rounded-full bg-amber-100 text-amber-800 px-1.5 py-0"
                         data-testid="uf-perms-dirty">unsaved</span>
                )}
              </div>
              <div className="rounded-lg border border-neutral-200 max-h-72 overflow-y-auto p-2 space-y-3">
                {grouped.map((g) => (
                  <div key={g.label}>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">
                      {g.label}
                    </div>
                    <div className="space-y-1">
                      {g.items.map((p) => (
                        <label key={p.code}
                                className="flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer hover:bg-neutral-50"
                                data-testid={`uf-perm-${p.code}`}>
                          <Checkbox checked={permsGranted.includes(p.code)}
                                     onCheckedChange={() => togglePerm(p.code)} />
                          <span className="text-xs text-neutral-800">{p.name || p.code}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {(!permCatalog.items || permCatalog.items.length === 0) && (
                  <div className="text-xs text-neutral-400 text-center py-4">
                    No workflow permissions defined.
                  </div>
                )}
              </div>
              <div className="text-[10px] text-neutral-500 mt-1">
                {permsGranted.length} granted · {permsSavePending ? "saving…" : "saved on Save"}
              </div>
            </div>

            <div data-testid="uf-distributors-panel">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Store className="h-4 w-4 text-[#6D2C91]" />
                <Label className="text-sm">Distributor Mappings</Label>
                {distDirty && (
                  <span className="text-[10px] rounded-full bg-amber-100 text-amber-800 px-1.5 py-0"
                         data-testid="uf-distributors-dirty">unsaved</span>
                )}
                {filteredDistributors.length > 0 && (
                  <button type="button"
                          onClick={() => {
                            const scopedIds = new Set(filteredDistributors.map((d) => d.id));
                            const allSelected = filteredDistributors.every((d) => distMapped.includes(d.id));
                            setDistMapped((m) => allSelected
                              ? m.filter((id) => !scopedIds.has(id))
                              : [...new Set([...m, ...scopedIds])]);
                          }}
                          className="ml-auto text-[11px] font-semibold text-[#6D2C91] underline decoration-dotted"
                          data-testid="uf-distributors-select-all">
                    {filteredDistributors.every((d) => distMapped.includes(d.id))
                      ? `Unselect all${distSearch ? " (filtered)" : ""}`
                      : `Select all${distSearch ? " (filtered)" : ""}`}
                  </button>
                )}
              </div>
              <div className="rounded-lg border border-neutral-200 p-2 space-y-2">
                {distMapped.length > 0 && (
                  <div className="flex flex-wrap gap-1 pb-1 border-b border-neutral-100"
                        data-testid="uf-distributor-chips">
                    {distMapped.map((id) => (
                      <span key={id}
                             className="inline-flex items-center gap-1 rounded-md border border-[#6D2C91]/40 bg-[#6D2C91]/5 px-2 py-0.5 text-xs text-[#6D2C91]"
                             data-testid={`uf-distributor-chip-${id}`}>
                        {distName(id)}
                        <button type="button" onClick={() => toggleDist(id)}
                                 className="rounded p-0.5 hover:bg-[#6D2C91]/15"
                                 aria-label="Remove distributor mapping"
                                 data-testid={`uf-distributor-remove-${id}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search className="h-3 w-3 text-neutral-400 absolute top-2.5 left-2" />
                  <Input value={distSearch} onChange={(e) => setDistSearch(e.target.value)}
                          placeholder="Search distributors…"
                          className="h-8 text-xs pl-7"
                          data-testid="uf-distributor-search" />
                </div>
                <div className="max-h-56 overflow-y-auto space-y-1"
                      data-testid="uf-distributor-options">
                  {filteredDistributors.length === 0 && (
                    <div className="text-xs text-neutral-400 text-center py-4">No matches</div>
                  )}
                  {filteredDistributors.map((d) => (
                    <label key={d.id}
                            className="flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer hover:bg-neutral-50"
                            data-testid={`uf-distributor-option-${d.id}`}>
                      <Checkbox checked={distMapped.includes(d.id)}
                                 onCheckedChange={() => toggleDist(d.id)} />
                      <span className="text-xs text-neutral-800">
                        <span className="font-mono text-[10px] mr-1 text-neutral-500">{d.code}</span>
                        {d.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="text-[10px] text-neutral-500 mt-1">
                {distMapped.length} mapped · {distSavePending ? "saving…" : "saved on Save"}
              </div>
            </div>
          </div>

          {!isEdit && (
            <div>
              <Label>Initial password</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="uf-initial-password" value={form.initial_password}
                        onChange={(e) => setForm({ ...form, initial_password: e.target.value })}
                        placeholder="Leave blank to auto-generate" className="h-10" />
                <Button type="button" variant="outline" onClick={genPassword} data-testid="uf-gen-password">
                  Generate
                </Button>
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">
                If left blank, the server generates a secure temporary password. Either way, the value is shown once after creation.
              </p>
            </div>
          )}

          {error && (
            <div className="text-sm rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2" data-testid="uf-error">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit"
                    disabled={busy || !form.name || !form.role_code}
                    className="tulsi-gradient text-white font-semibold"
                    data-testid="uf-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {isEdit ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    {/* Confirm dialog: soft-warn on active-data area removal */}
    <AlertDialog open={!!confirmArea} onOpenChange={(v) => !v && setConfirmArea(null)}>
      <AlertDialogContent data-testid="uf-area-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove area mapping?</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="font-medium">{confirmArea?.name}</span> has {confirmArea?.recentCount} recent record(s)
            (visits + attendance) tied to this user. Removing the mapping does not delete those records,
            but the user will no longer be assignable to this area for future work.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="uf-area-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="uf-area-confirm-remove"
            onClick={() => { toggleArea(confirmArea.id); setConfirmArea(null); }}
            className="bg-red-600 hover:bg-red-700">
            Remove anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
